import { FAMILY, type HtmlPageAppFont } from '@app/shared'
import {
  type FontFace,
  type FontReference,
  localFontReferences,
} from '@segnavia/format'
import type {
  LocalFonts,
  LocalFontUnavailableReason,
} from '@segnavia/frame/local-fonts'
import {
  CircleCheck,
  CircleDashed,
  type LucideIcon,
  TriangleAlert,
  Type,
} from 'lucide-react'
import { Button } from '../Button'
import { Notice } from '../index'
import { Table } from '../Table'
import {
  LOCAL_FONT_ACCESS_NOTE,
  type LocalFontAccess,
} from './local-page-fonts'
import './page-fonts.css'

type FontRow = {
  key: string
  family: string
  style: string
  status: FontStatus
}

type FontStatus =
  | 'ready'
  | 'included'
  | 'checking'
  | 'off'
  | LocalFontUnavailableReason

const STATUS: Record<
  FontStatus,
  { label: string; tone: 'success' | 'warning' | 'muted'; icon: LucideIcon }
> = {
  ready: { label: 'Prints', tone: 'success', icon: CircleCheck },
  included: { label: 'Prints', tone: 'success', icon: CircleCheck },
  checking: { label: 'Checking', tone: 'muted', icon: CircleDashed },
  off: { label: 'Turned off', tone: 'warning', icon: TriangleAlert },
  denied: { label: 'Needs permission', tone: 'warning', icon: TriangleAlert },
  unsupported: { label: 'Not shared', tone: 'warning', icon: TriangleAlert },
  'not-installed': {
    label: 'Not installed',
    tone: 'warning',
    icon: TriangleAlert,
  },
  unreadable: { label: 'Unreadable', tone: 'warning', icon: TriangleAlert },
}

const WEIGHT: Record<number, string> = {
  100: 'Thin',
  200: 'Extra Light',
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'Semibold',
  700: 'Bold',
  800: 'Extra Bold',
  900: 'Black',
}

/** A face named the way a font menu names it: Bold Italic, never 700 italic. */
export function faceStyle(face: Pick<FontFace, 'weight' | 'style'>): string {
  const weight = WEIGHT[Number(face.weight ?? 400)] ?? String(face.weight)
  if (face.style !== 'italic') return weight
  return weight === 'Regular' ? 'Italic' : `${weight} Italic`
}

export function fontRows(
  references: readonly FontReference[],
  localFonts: LocalFonts,
): FontRow[] {
  /* A family named twice — say with two fallbacks — is still one font. */
  const named = new Set<string>()
  return references.flatMap((reference): FontRow[] => {
    const name = 'app' in reference ? `app ${reference.app}` : reference.local
    if (named.has(name)) return []
    named.add(name)
    if ('app' in reference) {
      const family = FAMILY[reference.app as HtmlPageAppFont] ?? reference.app
      return [{ key: `app ${family}`, family, style: '', status: 'included' }]
    }
    const family = reference.local
    const missing = localFonts.unavailable.find(
      (font) => font.family === family,
    )
    if (missing) {
      return [
        { key: `local ${family}`, family, style: '', status: missing.reason },
      ]
    }
    const faces = localFonts.faces
      .filter((face) => face.family === family)
      .sort(
        (a, b) =>
          Number(a.weight ?? 400) - Number(b.weight ?? 400) ||
          (a.style === 'italic' ? 1 : 0) - (b.style === 'italic' ? 1 : 0),
      )
    if (faces.length === 0) {
      return [{ key: `local ${family}`, family, style: '', status: 'checking' }]
    }
    return faces.map((face) => {
      const style = faceStyle(face)
      return {
        key: `local ${family} ${style}`,
        family,
        style,
        status: 'ready',
      }
    })
  })
}

/** Every font a page names, face by face, and whether the PDF carries it. */
export function PageFonts({
  references,
  localFonts,
  access,
  onAsk,
}: {
  references: readonly FontReference[]
  localFonts: LocalFonts
  access: LocalFontAccess | null
  onAsk: () => void
}) {
  const names = localFontReferences(references).length > 0
  return (
    <div className="page-fonts">
      {!names ? null : access === 'prompt' || access === 'off' ? (
        <Notice icon={Type}>
          <span className="page-fonts__ask">
            Allow this site to use the fonts on this computer, and the PDF
            prints in them.
            <Button variant="secondary" size="sm" onClick={onAsk}>
              Use fonts on this computer
            </Button>
          </span>
        </Notice>
      ) : access && LOCAL_FONT_ACCESS_NOTE[access] ? (
        <Notice icon={Type}>{LOCAL_FONT_ACCESS_NOTE[access]}</Notice>
      ) : null}
      <Table
        compact
        columns={[
          { key: 'font', header: 'Font', cell: (row) => row.family },
          { key: 'style', header: 'Style', cell: (row) => row.style },
          {
            key: 'status',
            header: 'Status',
            cell: (row) => {
              const { label, tone, icon: Icon } = STATUS[row.status]
              return (
                <span className="page-fonts__status" data-tone={tone}>
                  <Icon size={14} aria-hidden />
                  {label}
                </span>
              )
            },
          },
        ]}
        rows={fontRows(references, localFonts).map((row) =>
          // The reader's own switch is what stands in the way, not the browser.
          access === 'off' && row.status === 'denied'
            ? { ...row, status: 'off' as const }
            : row,
        )}
        rowKey={(row) => row.key}
      />
    </div>
  )
}

/**
 * The first font by name and the rest by count, so the sentence keeps to one
 * line however many fonts are off: the Fonts view is where every one is named.
 */
export function fontsNoticeText(families: readonly string[]): string {
  const [first, ...rest] = families
  if (rest.length === 0) return `${first} does not print as designed.`
  const others = rest.length === 1 ? 'other font' : 'other fonts'
  return `${first} and ${rest.length} ${others} do not print as designed.`
}

/**
 * Over the sheet, only that fonts are off; which and why is the Fonts view's.
 * Where no band is drawn there is no view to show, so no way to it is offered.
 */
export function PageFontsNotice({
  families,
  onShow,
  onDismiss,
}: {
  families: readonly string[]
  onShow: (() => void) | null
  onDismiss: () => void
}) {
  return (
    <Notice icon={Type} compact onDismiss={onDismiss}>
      <span className="page-fonts-notice">
        <span className="page-fonts-notice__line">
          {fontsNoticeText(families)}
        </span>
        {onShow ? (
          <Button variant="quiet" size="sm" onClick={onShow}>
            Show fonts
          </Button>
        ) : null}
      </span>
    </Notice>
  )
}
